/**
 * sessionManager.ts
 * 前端会话管理模块（支持多标签同步、防抖写入）
 */

const SESSION_KEY = 'front_session_id';
const LAST_ACTIVE_KEY = 'last_active_time';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30分钟
const CHECK_INTERVAL = 60 * 1000; // 每分钟检查
const WRITE_DEBOUNCE = 2000; // 2秒内不重复写入

let lastWriteTime = 0;
let sessionExpiredCallbacks: Array<() => void> = [];

/** 生成随机 sessionId */
function generateSessionId() {
  return crypto.randomUUID();
}

/** 写入 localStorage（防抖） */
function safeWrite(key: string, value: string) {
  const now = Date.now();
  if (now - lastWriteTime > WRITE_DEBOUNCE) {
    localStorage.setItem(key, value);
    lastWriteTime = now;
  }
}

/** 通知所有注册的回调 */
function notifySessionExpired() {
  sessionExpiredCallbacks.forEach((cb) => cb());
}

/** 注册 session 过期回调 */
export function onSessionExpired(callback: () => void) {
  sessionExpiredCallbacks.push(callback);
}

/** 获取当前 sessionId */
export function getSessionId() {
  return localStorage.getItem(SESSION_KEY);
}

/** 初始化 session（包含多标签同步） */
export function initSession() {
  let sessionId = localStorage.getItem(SESSION_KEY);
  let lastActive = Number(localStorage.getItem(LAST_ACTIVE_KEY));
  const now = Date.now();

  // 若首次或超时则重置
  if (!sessionId || now - lastActive > SESSION_TIMEOUT) {
    sessionId = generateSessionId();
    safeWrite(SESSION_KEY, sessionId);
  }

  safeWrite(LAST_ACTIVE_KEY, now.toString());

  /** 更新活跃时间 */
  const updateActiveTime = () => {
    safeWrite(LAST_ACTIVE_KEY, Date.now().toString());
  };

  ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
    window.addEventListener(event, updateActiveTime, { passive: true });
  });

  /** 定时检查是否过期 */
  setInterval(() => {
    const lastActive = Number(localStorage.getItem(LAST_ACTIVE_KEY));
    const now = Date.now();

    if (now - lastActive > SESSION_TIMEOUT) {
      const newId = generateSessionId();
      safeWrite(SESSION_KEY, newId);
      safeWrite(LAST_ACTIVE_KEY, now.toString());
      // console.warn('[SessionManager] 会话过期，已重新生成 sessionId:', newId);
      notifySessionExpired();
    }
  }, CHECK_INTERVAL);

  /** 多标签页同步 */
  window.addEventListener('storage', (e) => {
    if (e.key === SESSION_KEY && e.newValue && e.newValue !== sessionId) {
      sessionId = e.newValue;
      // console.info('[SessionManager] 多标签同步 sessionId:', sessionId);
    }
  });

  // console.info('[SessionManager] 当前 sessionId:', sessionId);
  return sessionId;
}
